import loadable from "@loadable/component";
import { inject } from "mobx-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Store } from "../../../../client/stores";
import type { AptivadaAppFragment, BlockFragment } from "@ihr-radioedit/inferno-webapi";
import { abTranslator } from "../../lib/abHelper";
import { slugify } from "@ihr-radioedit/inferno-core";
import { getContestMediaServerUrl } from "../../lib/image";
import { hasPage, reverseRoute } from "../../lib/route";
import { Card, Container, Grid, Heading, MagicLink } from "../../ui";
import "./ContestsTile.style.scss";

const ContentTile = loadable(() => import("../../components/tiles/ContentTile.component"));

interface ContestTileProps {
    block: BlockFragment;
    contests: AptivadaAppFragment[];
    store?: Store;
}

export const ContestsTile = inject("store")(({ contests, store, block }: ContestTileProps) => {
    if (!store) {
        return null;
    }
    const { site, env } = store;
    useEffect(() => store.storeBlock(block));

    const hasPromotions = hasPage(site, "promotions");
    const listUrl = reverseRoute(site, "promotions") || reverseRoute(site, "contests") || "#";
    const detailUrl = (slug: string) =>
        reverseRoute(site, "promotion", { slug }) || reverseRoute(site, "contest", { slug }) || "#";
    const { t } = useTranslation();

    if (!contests.length) {
        return null;
    }
    return (
        <Container className="component-contests-tile">
            <Heading
                level={2}
                link={{
                    text: t("view_more"),
                    label: `${t("view_more")} ${
                        hasPromotions ? abTranslator("promotions", store?.testGroups, t) : t("contests")
                    }`,
                    url: listUrl,
                }}
            >
                <MagicLink to={listUrl} sectionName="contests" context="view_more">
                    {hasPromotions ? abTranslator("promotions", store?.testGroups, t) : t("contests")}
                </MagicLink>
            </Heading>
            <Grid columns={4}>
                {contests.slice(0, 4).map((contest: AptivadaAppFragment, i) => {
                    const slug = `${slugify(contest.title.trim(), 6)}-${contest.parentAppId || contest.appId}`;
                    return (
                        <Card key={i}>
                            <ContentTile
                                title={contest.title}
                                url={contest.gridRedirect || `${detailUrl(slug)}`}
                                slug={`contest_${i}`}
                                thumb={
                                    contest.displayImage
                                        ? getContestMediaServerUrl(env.IMAGE_HOST, contest.displayImage)
                                        : ""
                                }
                            />
                        </Card>
                    );
                })}
            </Grid>
        </Container>
    );
});

export default ContestsTile;
